import { Checkbox, FormControlLabel, Popover } from "@mui/material";
import { Theme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";

const useStyles = makeStyles((theme: Theme) => ({
  filterMenuContainer: {
    padding: "8px 16px",
    minWidth: 200,
    width: "100%",
    maxHeight: 400,
    overflow: "auto"
  },
  selectAllItem: {
    marginBottom: 8,
    borderBottom: "1px solid #e0e0e0",
    paddingBottom: 8
  },
  filterMenuItem: {
    display: "block"
  }
}));

const NONE_VALUE = "__NONE__";

interface RoleFilterMenuProps<T extends string> {
  open: boolean;
  onClose: () => void;
  anchorPosition?: { top: number; left: number };
  existingRoles: T[];
  selectedRoles: T[];
  onRoleFilterChange: (
    filterValues: T[] | null,
    filterType: "include" | "exclude" | undefined | null
  ) => void;
}

function RoleFilterMenu<T extends string>({
  open,
  onClose,
  anchorPosition,
  existingRoles,
  selectedRoles,
  onRoleFilterChange
}: RoleFilterMenuProps<T>) {
  const classNames = useStyles();

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      transformOrigin={{
        vertical: "top",
        horizontal: "right"
      }}
      PaperProps={{
        style: {
          boxShadow:
            "0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)"
        }
      }}
    >
      <div className={classNames.filterMenuContainer}>
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedRoles.length === existingRoles.length + 1}
              indeterminate={
                selectedRoles.length > 0 &&
                selectedRoles.length < existingRoles.length + 1
              }
              onChange={e => {
                if (e.target.checked) {
                  const allRoles = [...existingRoles, NONE_VALUE as T];
                  onRoleFilterChange(allRoles, "include");
                } else {
                  onRoleFilterChange(null, null);
                }
              }}
            />
          }
          label="Select All"
          className={classNames.selectAllItem}
        />
        {existingRoles.map(role => (
          <div key={role} className={classNames.filterMenuItem}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedRoles.includes(role)}
                  onChange={e => {
                    const newRoles = e.target.checked
                      ? [...selectedRoles, role]
                      : selectedRoles.filter(r => r !== role);
                    onRoleFilterChange(
                      newRoles.length > 0 ? newRoles : null,
                      "include"
                    );
                  }}
                />
              }
              label={role}
            />
          </div>
        ))}
        <div className={classNames.filterMenuItem}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedRoles.includes(NONE_VALUE as T)}
                onChange={e => {
                  const newRoles = e.target.checked
                    ? [...selectedRoles, NONE_VALUE as T]
                    : selectedRoles.filter(r => r !== (NONE_VALUE as T));
                  onRoleFilterChange(
                    newRoles.length > 0 ? newRoles : null,
                    "include"
                  );
                }}
              />
            }
            label="(None)"
          />
        </div>
      </div>
    </Popover>
  );
}

export default RoleFilterMenu;
